import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Search, Plus, Receipt, FileText, BookOpen } from 'lucide-react';
import StatCard from '../../components/data/StatCard';
import { vouchersApi } from '../../../lib/api';
import { useAppStore } from '../../../lib/store';
import { formatDate } from '../../../lib/dateUtils';

interface Voucher {
  id: string;
  voucher_number: string;
  voucher_type: 'receipt' | 'payment' | 'journal';
  status: 'draft' | 'posted' | 'cancelled';
  amount: number;
  currency?: string;
  description: string;
  created_at: string;
  property?: {
    title?: string;
    address?: string;
    property_code?: string;
  };
  account?: {
    account_name?: string;
    account_code?: string;
  };
}

export default function VouchersList() {
  const { t } = useTranslation(['finance', 'common']);
  const navigate = useNavigate();
  const user = useAppStore((state) => state.user);
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';
  
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const fetchVouchers = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: any = {};
      if (filterType !== 'all') filters.voucher_type = filterType;
      if (filterStatus !== 'all') filters.status = filterStatus;
      if (searchQuery) filters.search = searchQuery;

      const response = await vouchersApi.getAllWithSearch(filters);
      
      if (response.error) {
        throw new Error(response.error.message || 'Failed to load vouchers');
      }
      
      if (response.data) {
        setVouchers(response.data);
      } else {
        setVouchers([]);
      }
    } catch (err: any) {
      console.error('Error fetching vouchers:', err);
      setError(err.message || 'Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchers();
  }, [filterType, filterStatus, searchQuery]);

  const stats = {
    total: vouchers.length,
    receipts: vouchers.filter((v) => v.voucher_type === 'receipt').length,
    payments: vouchers.filter((v) => v.voucher_type === 'payment').length,
    drafts: vouchers.filter((v) => v.status === 'draft').length,
  };

  const typeColors = {
    receipt: 'success',
    payment: 'error',
    journal: 'info',
  } as const;

  const statusColors = {
    draft: 'default',
    posted: 'success',
    cancelled: 'error',
  } as const;

  const typeIcons = {
    receipt: <Receipt size={20} />,
    payment: <FileText size={20} />,
    journal: <BookOpen size={20} />,
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchVouchers}>
          {t('common:retry')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          {t('vouchers')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('subtitle')}
        </Typography>
      </Box>

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('totalTransactions')}
            value={stats.total}
            icon={<FileText />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('receipts')}
            value={stats.receipts}
            icon={<Receipt />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('payments')}
            value={stats.payments}
            icon={<FileText />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('voucherStatuses.draft')}
            value={stats.drafts}
            icon={<BookOpen />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={20} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('filterByType')}</InputLabel>
                <Select
                  value={filterType}
                  label={t('filterByType')}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <MenuItem value="all">{t('common:all')}</MenuItem>
                  <MenuItem value="receipt">{t('voucherTypes.receipt')}</MenuItem>
                  <MenuItem value="payment">{t('voucherTypes.payment')}</MenuItem>
                  <MenuItem value="journal">{t('voucherTypes.journal')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t('filterByStatus')}</InputLabel>
                <Select
                  value={filterStatus}
                  label={t('filterByStatus')}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">{t('common:all')}</MenuItem>
                  <MenuItem value="draft">{t('voucherStatuses.draft')}</MenuItem>
                  <MenuItem value="posted">{t('voucherStatuses.posted')}</MenuItem>
                  <MenuItem value="cancelled">{t('voucherStatuses.cancelled')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Plus size={20} />}
                sx={{ height: '56px' }}
                onClick={() => navigate('/dashboard/finance/vouchers/create')}
              >
                {t('addVoucher')}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Vouchers List */}
      <Grid container spacing={2}>
        {vouchers.map((voucher) => (
          <Grid item xs={12} key={voucher.id}>
            <Card 
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
              onClick={() => navigate(`/dashboard/finance/vouchers/${voucher.id}`)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {typeIcons[voucher.voucher_type]}
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {voucher.voucher_number}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      label={t(`voucherTypes.${voucher.voucher_type}`)}
                      color={typeColors[voucher.voucher_type]}
                      size="small"
                    />
                    <Chip
                      label={t(`voucherStatuses.${voucher.status}`)}
                      color={statusColors[voucher.status]}
                      size="small"
                    />
                  </Box>
                </Box>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  {voucher.description || '-'}
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t('amount')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {Number(voucher.amount).toLocaleString()} {voucher.currency || t('common:currency')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t('account')}
                    </Typography>
                    <Typography variant="body2">
                      {voucher.account?.account_name || voucher.account?.account_code || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t('date')}
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(voucher.created_at)}
                    </Typography>
                  </Grid>
                  {voucher.property && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        {t('property')}
                      </Typography>
                      <Typography variant="body2">
                        {voucher.property.title || voucher.property.address || voucher.property.property_code || '-'}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {vouchers.length === 0 && !loading && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('noTransactionsFound')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('adjustSearch')}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
